/**
 * PostHog initialization and helpers.
 * Use PostHogProvider and usePostHog() in components; call capture(client, …) for events.
 * Do not import the posthog-js default singleton outside initPostHog().
 */
import posthog from 'posthog-js';
import config from '../config';

const PH_IDENTIFIED_KEY = 'ph_identified_id';

/** PostHog reserved pageview event (manual SPA tracking). */
export const POSTHOG_PAGEVIEW_EVENT = '$pageview';

/** Parent event name for onboarding funnel analytics. Use with step and other properties. */
export const ONBOARDING_EVENT = 'on-boarding';

/** V2 onboarding agent + Financial Copilot chat — use `action` and `variant` (onboarding | copilot). */
export const V2_AGENT_EVENT = 'v2_agent';

/** Dashboard Copilot route (Financial Copilot vs Finance Q&A tabs). */
export const COPILOT_PAGE_EVENT = 'copilot_page_clicked';

/** Finance Q&A — same event name as existing captures (`action` + optional `embedded`). */
export const FINANCE_QA_EVENT = 'talk_to_reese_page_clicked';

/** Canonical product event names (avoid string drift across components). */
export const LOGGED_OUT_EVENT = 'logged_out';
export const SIGNED_IN_EVENT = 'signed_in';
export const SIGNUP_COMPLETED_EVENT = 'signup_completed';
export const DASHBOARD_ENTERED_EVENT = 'dashboard_entered';
export const HOME_PAGE_EVENT = 'home_page_clicked';
export const RESET_PASSWORD_PAGE_EVENT = 'reset_password_page_clicked';
export const FORGOT_PASSWORD_PAGE_EVENT = 'forgot_password_page_clicked';
export const MEET_AN_EXPERT_PAGE_EVENT = 'meet_an_expert_page_clicked';
/** Freeform note from the in-app feedback dock when no PostHog API survey is active. */
export const PRODUCT_FEEDBACK_EVENT = 'product_feedback';
export const WEB_VITALS_EVENT = 'web_vitals';
export const PASSWORD_RESET_VERIFIED_EVENT = 'password_reset_verified';
export const ACCOUNT_VERIFIED_EVENT = 'account_verified';

/**
 * Safe capture when PostHog is disabled (no provider), client is null, or capture is missing.
 * @param {import('posthog-js').PostHog | null | undefined} client
 * @param {string} eventName
 * @param {Record<string, unknown>} [properties]
 */
export function capture(client, eventName, properties) {
  if (!client || typeof client.capture !== 'function') return;
  client.capture(eventName, properties ?? {});
}

/**
 * Privacy-safe bucket for message length (no raw text).
 * @param {number} charCount
 * @returns {'empty' | '1_50' | '51_200' | '201_plus'}
 */
export function messageLengthBucket(charCount) {
  const n = Math.max(0, Number(charCount) || 0);
  if (n === 0) return 'empty';
  if (n <= 50) return '1_50';
  if (n <= 200) return '51_200';
  return '201_plus';
}

/**
 * Common props for Finance Q&A / embedded Reese chat (single event, `embedded` flag).
 * @param {boolean} embedded
 * @param {Record<string, unknown>} [extra]
 */
export function financeQaProps(embedded, extra = {}) {
  return { ...extra, embedded: Boolean(embedded) };
}

const LOCALHOST_ORIGIN = 'http://localhost:3000';

export function initPostHog() {
  if (typeof window === 'undefined' || !config.POSTHOG.KEY) {
    return null;
  }
  // Don't send events when running from localhost (e.g. production build run locally)
  if (window.location.origin === LOCALHOST_ORIGIN) {
    return null;
  }
  posthog.init(config.POSTHOG.KEY, {
    api_host: config.POSTHOG.HOST,
    person_profiles: 'identified_only',
    capture_pageview: false, // we capture pageviews via PostHogPageView for React Router
    capture_pageleave: true,
    autocapture: false, // no "clicked button", "submitted form", or other autocapture; we use explicit capture() only
    capture_performance: { web_vitals: false }, // do not send Web vitals events
    respect_dnt: true,
    persistence: 'localStorage+cookie',
  });
  return posthog;
}

/**
 * Stable distinct_id from token (never send raw token to PostHog).
 */
function hashForDistinctId(token) {
  if (!token || typeof token !== 'string') return null;
  let h = 0;
  for (let i = 0; i < token.length; i++) {
    const c = token.charCodeAt(i);
    h = (h << 5) - h + c;
    h |= 0;
  }
  return `user_${Math.abs(h).toString(36)}`;
}

/**
 * Identify user once per session. Skips if already identified with same id (avoids duplicate identify on Dashboard remounts).
 * Call clearIdentifiedSession() on logout.
 */
export function identifyUser(posthogClient, token, traits = {}) {
  if (!posthogClient || !token) return;
  const distinctId = hashForDistinctId(token);
  if (!distinctId) return;
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(PH_IDENTIFIED_KEY) === distinctId) {
      return;
    }
    posthogClient.identify(distinctId, traits);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(PH_IDENTIFIED_KEY, distinctId);
    }
  } catch (e) {
    // ignore storage/identify errors
  }
}

/** Call on logout so next login identifies correctly. */
export function clearIdentifiedSession() {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(PH_IDENTIFIED_KEY);
  } catch (e) {
    // ignore
  }
}
